import { useEffect, useRef, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import InlineLoader from '../components/Loader/InlineLoader';
import Table from '../components/Listing/Table';
import Modal from '../components/Modal/Modal';
import Button from '../components/Form/Button';
import Form from '../components/Form/Form';
import InputProduct from '../components/Form/Fields/InputProduct';
import LaboratoryLogo from '../components/Laboratory/LaboratoryLogo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation, faFileImage, faLink, faPlus, faTrash, faSpinner, faMagnifyingGlass, faXmark, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate, formatFullDate } from '../utils/FormatDate';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import './PurchaseOrderImportDetails.scss';

const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    fontSize: 13,
    padding: '5px 7px',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    outline: 'none',
};

const STATUS_MAP = {
    PENDING:   { labelKey: 'purchase_order_imports.status.pending',   bg: '#fef3c7', color: '#d97706', icon: faSpinner, spin: true },
    PROCESSED: { labelKey: 'purchase_order_imports.status.processed', bg: '#fef3c7', color: '#d97706', icon: faMagnifyingGlass },
    CONFIRMED: { labelKey: 'purchase_order_imports.status.confirmed', bg: '#dcfce7', color: '#16a34a', icon: faCircleCheck },
    FAILED:    { labelKey: 'purchase_order_imports.status.failed',    bg: '#fee2e2', color: '#dc2626', icon: faTriangleExclamation },
};

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const s = STATUS_MAP[status] || STATUS_MAP.PENDING;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: s.bg, color: s.color,
            padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600,
        }}>
            <FontAwesomeIcon icon={s.icon} spin={s.spin} style={{ fontSize: 12 }} />
            {t(s.labelKey)}
        </span>
    );
};

const formatAmount = (v) => v === null || v === undefined ? '—'
    : new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

export default function PurchaseOrderImportDetails() {
    const { id } = useParams();
    const { t } = useTranslation();
    const { setTitle, setTitleAvatar, setTitleBadge } = usePageTitle();
    const { setBreadcrumb } = useBreadcrumb();
    const [purchaseOrderImport, setPurchaseOrderImport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [autoMatchStarted, setAutoMatchStarted] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [addingLine, setAddingLine] = useState(false);
    const autoMatchRunFor = useRef(null);
    const tableRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/api/v1/purchase-order-imports/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setPurchaseOrderImport(data);
                const title = `BA-${data.id}`;
                setTitle(
                    `${title}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;" title="${formatFullDate(data.createdAt)}">${formatRelativeDate(data.createdAt)}</span>`
                );
                document.title = title;
                setBreadcrumb([
                    { label: t('Purchase Orders'), url: '/purchase-order-imports' },
                    { label: title },
                ]);

                const thumbUrl = `${API_BASE_URL}/api/v1/purchase-order-imports/${id}/file`;
                const isImg = (data.contentType || '').startsWith('image');
                setTitleAvatar(
                    isImg ? (
                        <button
                            type="button"
                            onClick={() => setShowImagePreview(true)}
                            title={t('purchase_order_imports.action.view_file')}
                            style={{
                                padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                                borderRadius: '50%', overflow: 'hidden', width: 36, height: 36, flexShrink: 0,
                            }}
                        >
                            <img src={thumbUrl} alt={t('purchase_order_imports.label.file')} style={{ width: 36, height: 36, objectFit: 'cover', display: 'block' }} />
                        </button>
                    ) : (
                        <div style={{
                            width: 36, height: 36, borderRadius: '50%', background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', flexShrink: 0,
                        }}>
                            <FontAwesomeIcon icon={faFileImage} style={{ fontSize: 14 }} />
                        </div>
                    )
                );
            })
            .catch(console.error)
            .finally(() => setLoading(false));
        return () => setTitleAvatar(null);
    }, [id, setTitle, setBreadcrumb, setTitleAvatar, t]);

    // Kept in sync with the status separately from the effect above, since the status
    // changes later on (OCR completes, confirm, etc.) without the id-keyed effect re-running.
    useEffect(() => {
        if (!purchaseOrderImport) return;
        setTitleBadge(<StatusBadge status={purchaseOrderImport.status} />);
        return () => setTitleBadge(null);
    }, [purchaseOrderImport?.status, setTitleBadge]);

    // OCR extraction runs asynchronously after upload — poll while it's still in progress so the
    // page updates on its own instead of requiring a manual refresh.
    useEffect(() => {
        if (purchaseOrderImport?.status !== 'PENDING') return;
        const interval = setInterval(() => {
            axios.get(`${API_BASE_URL}/api/v1/purchase-order-imports/${id}`, { withCredentials: true })
                .then(({ data }) => setPurchaseOrderImport(data))
                .catch(() => clearInterval(interval));
        }, 2000);
        return () => clearInterval(interval);
    }, [id, purchaseOrderImport?.status]);

    const { hasRight } = useAuth();
    const canEdit = hasRight('BONS_EDIT');
    const isProcessed = purchaseOrderImport?.status === 'PROCESSED';
    // Editable cells (product picker, qty/price/expiration/designation inputs, delete-line,
    // add-line, confirm) all hit BONS_EDIT-gated endpoints — without the right, the row falls
    // back to its read-only display even while the import is still in the editable PROCESSED
    // status, and the add/confirm action bar at the bottom is hidden entirely.
    const isEditable = isProcessed && canEdit;
    const lines = purchaseOrderImport?.lines || [];
    const matchedCount = lines.filter((l) => l.matchedProductId).length;

    const patchLine = async (lineId, payload) => {
        const { data } = await axios.patch(
            `${API_BASE_URL}/api/v1/purchase-order-imports/${id}/lines/${lineId}`,
            payload,
            { withCredentials: true }
        );
        setPurchaseOrderImport(data);
        return data;
    };

    const handleFieldBlur = (line, field, value) => {
        const current = field === 'unitPrice' ? line.unitPrice : line[field];
        if (String(current ?? '') === String(value ?? '')) return;
        patchLine(line.id, { [field]: value === '' ? null : value }).catch(() => {
            toast.error(t('purchase_order_import_details.toast.line_update_error'));
        });
    };

    const handleSelectProduct = (line, option) => {
        const payload = { matchedProductId: option ? option.value : null };
        // Manual lines have no OCR'd designation to fall back on — seed it from the picked
        // product's name so the line isn't left blank once matched.
        if (option && !line.designation) {
            payload.designation = option.label;
        }
        patchLine(line.id, payload).catch(() => {
            toast.error(t('purchase_order_import_details.toast.line_select_error'));
        });
    };

    const handleAddLine = async () => {
        setAddingLine(true);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/api/v1/purchase-order-imports/${id}/lines`, {}, { withCredentials: true });
            setPurchaseOrderImport(data);
            // Wait for the new row to actually be in the DOM (next paint) before scrolling to it.
            requestAnimationFrame(() => tableRef.current?.scrollToBottom());
        } catch (err) {
            toast.error(t('purchase_order_import_details.toast.add_line_error'));
        } finally {
            setAddingLine(false);
        }
    };

    const handleDeleteLine = async (lineId) => {
        try {
            const { data } = await axios.delete(`${API_BASE_URL}/api/v1/purchase-order-imports/${id}/lines/${lineId}`, { withCredentials: true });
            setPurchaseOrderImport(data);
        } catch (err) {
            toast.error(t('purchase_order_import_details.toast.delete_line_error'));
        }
    };

    // Try to resolve each still-unmatched line to a product automatically — covers imports
    // reached directly from the list without ever going through the upload modal's auto-match.
    useEffect(() => {
        if (!isProcessed || !canEdit || autoMatchStarted || lines.length === 0 || autoMatchRunFor.current === id) return;
        setAutoMatchStarted(true);
        autoMatchRunFor.current = id;

        (async () => {
            for (const line of lines) {
                if (line.matchedProductId || !line.designation) continue;
                try {
                    const { data } = await axios.get(`${API_BASE_URL}/api/v1/products`, {
                        params: { page: 0, size: 5, keyword: line.designation, prefixMatch: true },
                        withCredentials: true,
                    });
                    const content = data.content || [];
                    if (content.length === 1) {
                        await patchLine(line.id, { matchedProductId: content[0].id });
                    }
                } catch (err) {
                    console.error('Auto-match failed for line', line.id, err);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isProcessed, autoMatchStarted, lines.length, id]);

    const handleConfirm = async () => {
        if (matchedCount === 0) {
            toast.warning(t('purchase_order_import_details.toast.confirm_no_match'));
            return;
        }
        setConfirming(true);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/api/v1/purchase-order-imports/${id}/confirm`, {}, { withCredentials: true });
            setPurchaseOrderImport(data);
            toast.success(t('purchase_order_import_details.toast.confirm_success', { count: matchedCount, s: matchedCount > 1 ? 's' : '' }));
        } catch (err) {
            toast.error(err?.response?.data?.message || t('purchase_order_import_details.toast.confirm_error'));
        } finally {
            setConfirming(false);
        }
    };

    if (loading) return <InlineLoader loadingMessage={t('Loading...')} />;
    if (!purchaseOrderImport) return <div className="page-content text-muted p-4">{t('purchase_order_import_details.not_found')}</div>;

    const columns = [
        {
            Header: t('ID'),
            accessor: 'matchedProductIdColumn',
            width: 70,
            minWidth: 70,
            className: 'text-center',
            Cell: (row) => row.matchedProductId
                ? <span style={{ fontSize: 13, fontWeight: 500, color: '#9ca3af' }}>{row.matchedProductId}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            Header: t('purchase_order_import_details.column.matched_product'),
            accessor: 'matchedProductId',
            minWidth: '320px',
            Cell: (row) => {
                // Matched product's display fields are embedded directly on the line by the
                // backend (single batch lookup) — no per-row fetch, so this is always in sync
                // with matchedProductId, including right after an auto-match/manual pick.
                const product = row.matchedProductId
                    ? {
                        id: row.matchedProductId, name: row.matchedProductName,
                        presentation: row.matchedProductPresentation, dosage: row.matchedProductDosage,
                        imageUrl: row.matchedProductImageUrl,
                        laboratoryId: row.matchedProductLaboratoryId, laboratoryName: row.matchedProductLaboratoryName,
                    }
                    : null;

                if (isEditable) {
                    return (
                        <Form
                            id={`po-line-product-${row.id}`}
                            key={`${row.id}-${row.matchedProductId ?? 'none'}`}
                            defaultValues={{ product: row.matchedProductId ?? '' }}
                            onSubmit={() => {}}
                        >
                            <InputProduct
                                name="product"
                                placeholder={t('purchase_order_import_details.field.choose_product_placeholder')}
                                initialKeyword={row.matchedProductId ? '' : (row.designation ?? '')}
                                handleChange={(option) => handleSelectProduct(row, option)}
                            />
                        </Form>
                    );
                }

                if (!product) return <span className="cell-muted">{t('purchase_order_import_details.label.unmatched')}</span>;
                const sub = [product.presentation, product.dosage].filter(Boolean).join(' · ');
                return (
                    <div style={{ minWidth: 0, maxWidth: 300 }}>
                        <NavLink
                            to={`/products/${product.id}`}
                            className="cell-name"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            data-tooltip-id="tooltip"
                            data-tooltip-content={product.name}
                        >
                            {product.name}
                        </NavLink>
                        {sub && (
                            <span
                                className="cell-sub"
                                style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 5, color: '#111827' }}
                                data-tooltip-id="tooltip"
                                data-tooltip-content={sub}
                            >
                                {sub}
                            </span>
                        )}
                        {product.laboratoryName && (
                            <NavLink
                                to={`/suppliers/${product.laboratoryId}`}
                                className="cell-sub"
                                style={{ textDecoration: 'none', color: '#374151', display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {t('By')} <LaboratoryLogo id={product.laboratoryId} name={product.laboratoryName} size={16} />
                                {product.laboratoryName}
                            </NavLink>
                        )}
                    </div>
                );
            },
        },
        {
            Header: t('purchase_order_import_details.column.qty'),
            accessor: 'quantity',
            width: 100,
            minWidth: 100,
            className: 'text-end',
            Cell: (row) => isEditable ? (
                <input
                    type="number"
                    min={0}
                    defaultValue={row.quantity ?? ''}
                    onBlur={(e) => handleFieldBlur(row, 'quantity', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                    style={inputStyle}
                />
            ) : (
                row.quantity != null ? (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        minWidth: 26, padding: '2px 8px', borderRadius: 999,
                        background: '#eff6ff', color: '#1d4ed8', fontSize: 11.5, fontWeight: 700,
                    }}>
                        {row.quantity}
                    </span>
                ) : '—'
            ),
        },
        {
            Header: t('purchase_order_import_details.column.unit_price'),
            accessor: 'unitPrice',
            width: 100,
            minWidth: 100,
            className: 'text-end',
            Cell: (row) => isEditable ? (
                <input
                    type="number"
                    step="0.01"
                    min={0}
                    defaultValue={row.unitPrice ?? ''}
                    onBlur={(e) => handleFieldBlur(row, 'unitPrice', e.target.value === '' ? null : parseFloat(e.target.value))}
                    style={inputStyle}
                />
            ) : (
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 4 }}>
                    <span style={{ fontSize: 13.5, color: '#111827' }}>{formatAmount(row.unitPrice)}</span>
                    <span style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                </div>
            ),
        },
        {
            Header: t('purchase_order_import_details.column.public_price'),
            accessor: 'publicPrice',
            width: 130,
            minWidth: 130,
            className: 'text-end',
            Cell: (row) => {
                const priceMismatch = row.matchedProductId && row.matchedProductPublicPrice != null && row.publicPrice != null
                    && Math.abs(Number(row.publicPrice) - Number(row.matchedProductPublicPrice)) > 0.01;
                const mismatchTitle = priceMismatch ? t('purchase_order_import_details.tooltip.catalog_price', { amount: formatAmount(row.matchedProductPublicPrice) }) : undefined;

                return isEditable ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                            type="number"
                            step="0.01"
                            min={0}
                            defaultValue={row.publicPrice ?? ''}
                            onBlur={(e) => handleFieldBlur(row, 'publicPrice', e.target.value === '' ? null : parseFloat(e.target.value))}
                            style={inputStyle}
                        />
                        {priceMismatch && (
                            <FontAwesomeIcon icon={faTriangleExclamation} data-tooltip-id="tooltip" data-tooltip-content={mismatchTitle} style={{ color: '#d97706', flexShrink: 0 }} />
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 4 }}>
                        <span style={{ fontSize: 13.5, color: '#111827' }}>{formatAmount(row.publicPrice)}</span>
                        <span style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                        {priceMismatch && (
                            <FontAwesomeIcon icon={faTriangleExclamation} data-tooltip-id="tooltip" data-tooltip-content={mismatchTitle} style={{ color: '#d97706' }} />
                        )}
                    </div>
                );
            },
        },
        {
            Header: t('Expiration'),
            accessor: 'expirationDateRaw',
            width: 110,
            minWidth: 110,
            Cell: (row) => isEditable ? (
                <input
                    type="text"
                    placeholder={t('purchase_order_import_details.field.expiration_placeholder')}
                    defaultValue={row.expirationDateRaw ?? ''}
                    onBlur={(e) => handleFieldBlur(row, 'expirationDateRaw', e.target.value)}
                    style={inputStyle}
                />
            ) : (row.expirationDateRaw ?? '—'),
        },
        {
            Header: t('purchase_order_import_details.column.extracted_designation'),
            accessor: 'designation',
            visible: isProcessed,
            Cell: (row) => isEditable ? (
                <input
                    key={`designation-${row.id}-${row.designation ?? ''}`}
                    type="text"
                    defaultValue={row.designation ?? ''}
                    onBlur={(e) => handleFieldBlur(row, 'designation', e.target.value)}
                    style={inputStyle}
                />
            ) : (
                <span style={{ color: '#9ca3af', fontSize: 13 }}>{row.designation ?? '—'}</span>
            ),
        },
    ];

    if (isEditable) {
        columns.push({
            Header: '',
            accessor: 'lineActions',
            width: 50,
            minWidth: 50,
            Cell: (row) => (
                <button
                    type="button"
                    onClick={() => handleDeleteLine(row.id)}
                    title={t('purchase_order_import_details.action.delete_line')}
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 6, border: '1px solid #e5e7eb',
                        background: '#fff', color: '#9ca3af', cursor: 'pointer',
                    }}
                >
                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                </button>
            ),
        });
    }

    const fileUrl = `${API_BASE_URL}/api/v1/purchase-order-imports/${id}/file`;
    const isImage = (purchaseOrderImport.contentType || '').startsWith('image');
    const matchRatioColor = lines.length === 0 ? '#9ca3af' : matchedCount === lines.length ? '#16a34a' : matchedCount === 0 ? '#dc2626' : '#d97706';

    // Shown next to the table's results counter — the date now lives in the page title
    // subtitle instead (see the effect above), and the status badge next to the title itself.
    const matchRatioChip = (
        <InfoChip icon={faLink} color={matchRatioColor}>
            {t('purchase_order_import_details.matched_ratio', { matched: matchedCount, total: lines.length, s: lines.length !== 1 ? 's' : '' })}
        </InfoChip>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {isImage && (
                <Modal
                    show={showImagePreview}
                    onClose={() => setShowImagePreview(false)}
                    classSize="modal-dialog-centered"
                    dialogStyle={{ maxWidth: 720 }}
                >
                    <div style={{ position: 'relative' }}>
                        <button
                            type="button"
                            onClick={() => setShowImagePreview(false)}
                            title={t('Close')}
                            style={{
                                position: 'absolute', top: 10, right: 10,
                                width: 32, height: 32, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: 'none', background: 'rgba(17,24,39,0.55)', color: '#fff',
                                cursor: 'pointer', fontSize: 14,
                            }}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                        <img src={fileUrl} alt={t('purchase_order_imports.label.file')} style={{ width: '100%', borderRadius: 8, display: 'block' }} />
                    </div>
                </Modal>
            )}

            {purchaseOrderImport.status === 'FAILED' && (
                <div style={{
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: '#fef2f2', borderBottom: '1px solid #fecaca',
                    padding: '10px 20px', color: '#991b1b', fontSize: 13,
                }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    {t('purchase_order_import_details.error_analysis_fallback')}
                </div>
            )}

            {purchaseOrderImport.status === 'PENDING' ? (
                <div style={{
                    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: 12, color: '#6b7280',
                }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 22, color: '#d97706' }} />
                    <p style={{ fontSize: 13, margin: 0 }}>{t('purchase_order_import_details.analyzing')}</p>
                </div>
            ) : (
                /* Lines table — fills remaining height so infinite scroll works, header stays put */
                <div style={{ flex: 1, minHeight: 0 }}>
                    <Table
                        ref={tableRef}
                        columns={columns}
                        data={lines}
                        mode="client"
                        rowId="id"
                        rowClassName={(row) => row.matchedProductId ? 'row-matched' : ''}
                        searchPlaceholder={t('purchase_order_import_details.search_placeholder')}
                        pageSize={lines.length || 20}
                        emptyMessage={t('purchase_order_import_details.empty')}
                        counterExtra={matchRatioChip}
                    />
                </div>
            )}

            {isEditable && (
                <div style={{
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    background: '#f9fafb', borderTop: '1px solid #e5e7eb',
                    padding: '10px 20px', color: '#374151', fontSize: 13,
                }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#374151' }}>
                        {t('purchase_order_import_details.not_confirmed_yet')}
                        <FontAwesomeIcon
                            icon={faCircleQuestion}
                            data-tooltip-id="tooltip"
                            data-tooltip-content={t('purchase_order_import_details.tooltip.help')}
                            style={{ cursor: 'help', color: '#9ca3af' }}
                        />
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <Button
                            isLoading={addingLine}
                            loadingMessage={t('purchase_order_import_details.action.adding')}
                            className="btn-add-line"
                            type="button"
                            onClick={handleAddLine}
                        >
                            <span className="btn-add-line__icon">
                                <FontAwesomeIcon icon={faPlus} />
                            </span>
                            {t('purchase_order_import_details.action.add_line')}
                        </Button>
                        <Button
                            isLoading={confirming}
                            loadingMessage={t('purchase_order_import_details.action.creating_stock')}
                            className="btn btn-primary"
                            type="button"
                            onClick={handleConfirm}
                            disabled={matchedCount === 0}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {t('Apply')} ({matchedCount}/{lines.length})
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

const InfoChip = ({ icon, color, tooltip, children }) => (
    <span
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
        data-tooltip-id={tooltip ? 'tooltip' : undefined}
        data-tooltip-content={tooltip}
    >
        <span style={{
            width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
            background: `${color}1a`, color,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
        }}>
            <FontAwesomeIcon icon={icon} />
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>{children}</span>
    </span>
);
